export type PictureSizePreset =
  | 'full'
  | 'grid'
  | 'column-panel'
  | 'column-panel-wide'
  | 'two-up'
  | 'two-up-wide'
  | 'three-up'
  | 'three-up-wide'
  | 'four-up'
  | 'six-up'
  | 'article-card'
  | 'featured-card'
  | 'icon'

type Breakpoint = 'mobile' | 'tablet' | 'desktop' | 'desktopXL'

type getParamsArgs = {
  preset: PictureSizePreset
  breakpoint?: Breakpoint
  density?: 1 | 2
  quality?: string
  format?: 'webp' | 'jpg'
}

export function getParams({ preset, breakpoint = 'mobile', density = 1, quality, format = 'webp' }: getParamsArgs) {
  /**  PictureSizePresets : 1x size (ie Rendered img size) for each breakpoint */
  /*   sizes considered -
      Mobile:  iPhone Pro Max = 430px
      Tablet:  iPad Pro Portrait = 1024px
      Desktop: 1920px
      DesktopXL: Ultra wide office monitor: 3440px  @TODO  make layout stop at 2560px maybe?
  */
  // prettier-ignore
  const sizePresets = {
    'full':              { mobile: 430, tablet: 1024, desktop: 1920, desktopXL: 1920 },
    'grid':              { mobile: 430, tablet: 1024, desktop: 1248, desktopXL: 1248 },
    'column-panel':      { mobile: 430, tablet: 512,  desktop: 624,  desktopXL: 624 },
    'column-panel-wide': { mobile: 430, tablet: 512,  desktop: 960,  desktopXL: 1720 },
    'two-up':            { mobile: 430, tablet: 512,  desktop: 622,  desktopXL: 622 },
    'two-up-wide':       { mobile: 430, tablet: 512,  desktop: 954,  desktopXL: 1494 },
    'three-up':          { mobile: 430, tablet: 336,  desktop: 414,  desktopXL: 414 },
    'three-up-wide':     { mobile: 430, tablet: 336,  desktop: 635,  desktopXL: 995 },
    'four-up':           { mobile: 288, tablet: 245,  desktop: 309,  desktopXL: 309 },
    'six-up':            { mobile: 149, tablet: 254,  desktop: 205,  desktopXL: 205 },
    'article-card':      { mobile: 430, tablet: 430,  desktop: 430,  desktopXL: 430 }, /* ideal = 430, 366, 309, 309, but authored with small images */
    'featured-card':     { mobile: 430, tablet: 512,  desktop: 832,  desktopXL: 832 },
    'icon':              { mobile: 48,  tablet: 48,   desktop: 48,   desktopXL: 48 },
  } as const satisfies Record<PictureSizePreset, Record<Breakpoint, number>>

  const width = sizePresets[preset]?.[breakpoint] || 720
  const WebpParams = {
    fm: 'webp',
    fit: 'scale',
    q: quality,
    w: width * density,
  } as const

  const JpgParams = {
    fl: 'progressive',
    fit: 'scale',
    q: quality,
    w: width * density,
  } as const

  const params = format === 'webp' ? WebpParams : JpgParams

  // @ts-ignore
  return new URLSearchParams(params).toString()
}

type getSrcSetArgs = getParamsArgs & { src?: string }
export function getSrcSet({ src, preset, breakpoint = 'mobile', quality, format = 'webp' }: getSrcSetArgs) {
  if (!src) return ''

  const params2 = getParams({ preset, breakpoint, density: 2, quality, format })
  const srcSet2 = `${src}?${params2} 2x`
  const params1 = getParams({ preset, breakpoint, density: 1, quality, format })
  let srcSet1 = `${src}?${params1} 1x`
  if (breakpoint === 'desktopXL') {
    srcSet1 = srcSet2.replace('2x', '1x') // always serve XL images for desktopXL
  }

  return [srcSet2, srcSet1].join(', ')
}

const VIDEO_TYPES = new Map([
  ['mp4', 'video/mp4'],
  ['webm', 'video/webm'],
  ['mov', 'video/quicktime'],
])

// A browser skips a <source> whose type it can't play, so an unknown type is left off for it to sniff.
export function getVideoType(url: string = ''): string | undefined {
  if (url.startsWith('https://player.vimeo.com/')) return 'video/mp4'
  const extension = new URL(url, 'https://localhost').pathname.split('.').pop()?.toLowerCase() ?? ''
  return VIDEO_TYPES.get(extension)
}
